/**
 * Map native HealthKit / Health Connect workout JSON into the persisted
 * ``metrics`` object (fitplus-platform / Cloud Functions).
 *
 * Pace and speed are derived from distance + duration when not provided.
 * Missing / empty values are omitted.
 */

const NATIVE_TO_METRICS = [
	['avg_heart_rate', 'avgHeartRate'],
	['max_heart_rate', 'maxHeartRate'],
	['elevation_gain_m', 'elevationGainM'],
	['avg_power_w', 'avgPowerW'],
	['temperature_c', 'temperatureC'],
	['notes', 'notes'],
	['avg_pace_sec_per_km', 'avgPaceSecPerKm'],
	['avg_speed_kmh', 'avgSpeedKmh'],
];

function asFiniteNumber(value) {
	if (value === null || value === undefined || value === '') {
		return null;
	}
	const n = typeof value === 'number' ? value : Number(value);
	return Number.isFinite(n) ? n : null;
}

/**
 * @param {object} workout Native bridge workout object
 * @param {{ distanceKm?: number, durationSeconds?: number }} [overrides]
 * @returns {object|null} metrics dict or null when empty
 */
export function buildMetricsFromWorkout(workout, overrides = {}) {
	if (!workout || typeof workout !== 'object') {
		return null;
	}

	const metrics = {};

	for (const [src, dest] of NATIVE_TO_METRICS) {
		const raw = workout[src];
		if (raw === null || raw === undefined || raw === '') {
			continue;
		}
		if (dest === 'notes') {
			const text = String(raw).trim();
			if (text) {
				metrics.notes = text;
			}
			continue;
		}
		const num = asFiniteNumber(raw);
		if (num !== null) {
			metrics[dest] = num;
		}
	}

	const durationSeconds =
		asFiniteNumber(overrides.durationSeconds) ??
		asFiniteNumber(workout.duration);
	const distanceKm =
		asFiniteNumber(overrides.distanceKm) ??
		(workout.distance && asFiniteNumber(workout.distance.value) !== null
			? asFiniteNumber(workout.distance.value) / 1000
			: null);

	if (
		durationSeconds !== null &&
		durationSeconds > 0 &&
		distanceKm !== null &&
		distanceKm > 0
	) {
		if (metrics.avgPaceSecPerKm == null) {
			metrics.avgPaceSecPerKm = Math.round(durationSeconds / distanceKm);
		}
		if (metrics.avgSpeedKmh == null) {
			const hours = durationSeconds / 3600;
			metrics.avgSpeedKmh = Math.round((distanceKm / hours) * 100) / 100;
		}
	}

	return Object.keys(metrics).length ? metrics : null;
}
